import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useCheckout } from "../../hooks/useCheckout";
import { useAuth } from "../../contexts/AuthContext"; // To get user details for billing
import { formatCurrency } from "../../utils/formatters"; // Add formatCurrency import
import { useTranslation } from "react-i18next"; // Import the useTranslation hook
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { setError } from "../../store/slices/checkoutSlice";
import { toast } from "react-toastify";
import { PAYMENT_METHODS } from "../../config/constants";

// Styling for the CardElement (Reverted)
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  hidePostalCode: true, // Assuming postal code is collected elsewhere
};

// Helper function to copy text
const copyToClipboard = (text, feedbackMessage) => {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success(feedbackMessage || "Copied to clipboard!");
    },
    (err) => {
      toast.error("Failed to copy!");
      console.error("Could not copy text: ", err);
    }
  );
};

/**
 * Component to render Stripe Bank Transfer Instructions
 */
const StripeBankTransferInstructions = ({ nextAction }) => {
  const { t: tCheckout } = useTranslation("checkout");

  if (!nextAction || nextAction.type !== "display_bank_transfer_instructions") {
    return (
      <Alert severity="warning">
        {tCheckout("bankTransferInstructionsUnavailable")}
      </Alert>
    );
  }

  const instructions = nextAction.display_bank_transfer_instructions;
  const financialAddresses = instructions.financial_addresses || [];
  // Assuming we primarily use IBAN for eu_bank_transfer
  const ibanDetails = financialAddresses.find((addr) => addr.type === "iban");

  if (!ibanDetails) {
    return (
      <Alert severity="error">{tCheckout("couldNotRetrieveBankDetails")}</Alert>
    );
  }

  const amountRemaining = (instructions.amount_remaining / 100).toFixed(2);
  const currency = instructions.currency.toUpperCase();
  const reference = instructions.reference;
  const bankName = ibanDetails.iban.bank_name || "N/A";
  const iban = ibanDetails.iban.iban;
  const bic = ibanDetails.iban.bic || "N/A"; // BIC might not always be present
  const accountHolder =
    ibanDetails.iban.account_holder_name || "Stripe Payment"; // Default if missing

  // Get original amount from metadata if available (for currency conversion display)
  const originalAmount = instructions.metadata?.original_amount;
  const originalCurrency =
    instructions.metadata?.original_currency?.toUpperCase() || "CHF";
  const showConversion = originalAmount && currency !== originalCurrency;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 2, mb: 2, backgroundColor: "grey.50" }}
    >
      <Typography variant="h6" gutterBottom>
        {tCheckout("bankTransferInstructions")}
      </Typography>

      {showConversion ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {tCheckout("currencyConversionInfo", {
            originalAmount,
            originalCurrency,
            convertedAmount: amountRemaining,
            convertedCurrency: currency,
          })}
        </Alert>
      ) : null}

      <Typography variant="body1" paragraph>
        {tCheckout("bankTransferInstructionsExplanation")}
      </Typography>
      <List dense>
        <ListItem>
          <ListItemText
            primary={tCheckout("amountDue")}
            secondary={`${amountRemaining} ${currency}`}
          />
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <IconButton
              size="small"
              onClick={() =>
                copyToClipboard(amountRemaining, tCheckout("amountCopied"))
              }
              aria-label={tCheckout("copyAmount")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </ListItemIcon>
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText primary="Payment Reference" secondary={reference} />
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <IconButton
              size="small"
              onClick={() =>
                copyToClipboard(reference, tCheckout("referenceCopied"))
              }
              aria-label={tCheckout("copyReference")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </ListItemIcon>
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText primary="Beneficiary Name" secondary={accountHolder} />
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <IconButton
              size="small"
              onClick={() =>
                copyToClipboard(accountHolder, tCheckout("beneficiaryCopied"))
              }
              aria-label={tCheckout("copyBeneficiary")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </ListItemIcon>
        </ListItem>
        <Divider component="li" />
        <ListItem>
          <ListItemText primary="IBAN" secondary={iban} />
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(iban, tCheckout("ibanCopied"))}
              aria-label={tCheckout("copyIban")}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </ListItemIcon>
        </ListItem>
        {bic !== "N/A" && (
          <>
            <Divider component="li" />
            <ListItem>
              <ListItemText primary="BIC/SWIFT" secondary={bic} />
              <ListItemIcon sx={{ minWidth: "auto" }}>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(bic, tCheckout("bicCopied"))}
                  aria-label={tCheckout("copyBic")}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </ListItemIcon>
            </ListItem>
          </>
        )}
        {bankName !== "N/A" && (
          <>
            <Divider component="li" />
            <ListItem>
              <ListItemText primary="Bank Name" secondary={bankName} />
              {/* No copy icon for bank name usually */}
            </ListItem>
          </>
        )}
      </List>
      <Alert severity="info" sx={{ mt: 2 }}>
        {tCheckout("bankTransferInstructionsEnsure")}
      </Alert>
    </Paper>
  );
};

/**
 * Component to render Manual Bank Transfer Instructions
 * @param {Object} props - Component props
 * @param {string} props.instructions - The bank transfer instructions text
 * @returns {JSX.Element} The rendered component
 */
export const ManualBankTransferInstructions = ({ instructions }) => {
  const { t: tCheckout } = useTranslation("checkout");

  if (!instructions) {
    return (
      <Alert severity="warning">
        {tCheckout("manualBankTransferInstructionsUnavailable")}
      </Alert>
    );
  }

  // Split instructions into lines for better display and potential copying
  const lines = instructions.split("\n").map((line) => {
    const parts = line.split(":");
    return {
      label: parts[0] ? parts[0].trim() : "",
      value: parts.length > 1 ? parts.slice(1).join(":").trim() : "",
    };
  });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 2, mb: 2, backgroundColor: "grey.50" }}
    >
      <Typography variant="h6" gutterBottom>
        {tCheckout("manualBankTransferInstructions")}
      </Typography>
      <Typography variant="body1" paragraph>
        {tCheckout("manualBankTransferExplanation")}
      </Typography>

      {/* Display the formatted instructions */}
      {lines.map((line, index) =>
        line.label ? (
          <Box
            key={index}
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: "bold" }}
            >
              {line.label}:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              <Typography variant="body2" component="span">
                {line.value}
              </Typography>
              {line.value && (
                <IconButton
                  size="small"
                  onClick={() =>
                    copyToClipboard(
                      line.value,
                      tCheckout("lineCopied", { label: line.label })
                    )
                  }
                  sx={{ ml: 1 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        ) : (
          <Typography key={index} variant="body2" sx={{ mb: 1 }}>
            {line.value}
          </Typography>
        )
      )}

      <Button
        startIcon={<ContentCopyIcon />}
        onClick={() =>
          copyToClipboard(instructions, tCheckout("allInstructionsCopied"))
        }
        size="small"
        sx={{ mt: 2 }}
      >
        {tCheckout("copyAll")}
      </Button>
    </Paper>
  );
};

/**
 * Component for the Payment step in the checkout process.
 * Handles Stripe Payment Element or Bank Transfer instructions.
 * @param {object} props - Component props
 * @param {number | null} [props.amountDue] - The amount remaining to be paid after wallet deduction.
 * @param {Function} [props.onBankTransferSubmit] - Function to handle bank transfer submission
 */
const PaymentStep = ({ amountDue, onBankTransferSubmit }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { processPayment } = useCheckout();
  const { t: tCheckout } = useTranslation("checkout");
  const {
    checkoutData,
    clientSecret,
    loading,
    error,
    orderNumber,
    checkoutSession, // Keep this as it might contain other useful info
    paymentInstructions, // Explicitly get paymentInstructions
    nextAction, // Explicitly get nextAction
    activeStep, // Added this to fix the undefined reference
  } = useSelector((state) => state.checkout);

  // Local state specific to payment step
  const [paymentError, setPaymentError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  // Clear local payment error when global error changes or step changes
  useEffect(() => {
    setPaymentError(null);
  }, [error]);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setPaymentError(event.error.message);
    } else {
      setPaymentError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    setPaymentError(null);
    dispatch(setError(null)); // Clear global error too

    if (!stripe || !elements) {
      setPaymentError(tCheckout("paymentSystemNotReady"));
      setIsProcessing(false);
      return;
    }

    try {
      // Determine billing details for Stripe confirmation
      const billingDetails = {
        name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        email: user?.email,
        address: {
          line1: checkoutData.billingAddress.address,
          city: checkoutData.billingAddress.city,
          postal_code: checkoutData.billingAddress.postalCode,
          country: checkoutData.billingAddress.country, // This should now be the ISO code
        },
      };
      if (user?.phoneNumber) {
        billingDetails.phone = user.phoneNumber;
      }

      console.log("PaymentStep: Confirming payment with Stripe...");

      // Handle payment method-specific confirmation logic
      if (checkoutData.paymentMethod === PAYMENT_METHODS.TWINT) {
        console.log("Processing TWINT payment...");

        const returnUrl = `${window.location.origin}/checkout`;
        console.log(`TWINT payment: Using return URL: ${returnUrl}`);

        // Log parameters before making the call
        console.log("TWINT payment parameters:", {
          clientSecret: clientSecret
            ? `${clientSecret.substring(0, 10)}...`
            : "missing",
          billingDetails: billingDetails,
          returnUrl: returnUrl,
        });

        try {
          if (!clientSecret) {
            throw new Error("Missing client secret for TWINT payment");
          }

          const result = await stripe.confirmTwintPayment(clientSecret, {
            payment_method: {
              billing_details: billingDetails,
            },
            // Return URL where the customer should be redirected after the authorization
            return_url: returnUrl,
          });

          console.log("TWINT confirmation result:", result);

          const { error: stripeError, paymentIntent } = result;

          if (stripeError) {
            console.error("TWINT confirmation error:", stripeError);
            setPaymentError(
              stripeError.message ||
                "An unexpected error occurred during TWINT payment."
            );
            setIsProcessing(false);
            return;
          }

          // Note: If the flow is working correctly, this code might not be reached
          // as the user will be redirected to Stripe's TWINT page
          if (paymentIntent) {
            console.log(
              `TWINT PaymentIntent status: ${paymentIntent.status}, ID: ${paymentIntent.id}`
            );

            // Only process the payment if it doesn't require further action
            if (paymentIntent.status !== "requires_action") {
              await processPayment({
                paymentIntentId: paymentIntent.id,
              });

              console.log(`TWINT PaymentIntent processed: ${paymentIntent.id}`);
            } else {
              console.log(
                "TWINT payment requires further action, waiting for redirect..."
              );
            }
          }
        } catch (error) {
          console.error("TWINT payment error:", error);
          setPaymentError(
            error.message || "An error occurred initiating the TWINT payment."
          );
          setIsProcessing(false);
        }
      } else {
        // Original credit card flow
        const { error: stripeError, paymentIntent } =
          await stripe.confirmCardPayment(
            clientSecret, // The client secret of the PaymentIntent
            {
              payment_method: {
                card: elements.getElement(CardElement),
                billing_details: billingDetails,
              },
            }
          );

        if (stripeError) {
          console.error("Stripe confirmation error:", stripeError);
          setPaymentError(
            stripeError.message ||
              "An unexpected error occurred during payment."
          );
          setIsProcessing(false);
          return;
        }

        // If confirmation was successful (or requires further action handled by Stripe.js)
        if (paymentIntent) {
          console.log(
            `Stripe PaymentIntent status: ${paymentIntent.status}, ID: ${paymentIntent.id}`
          );
          if (
            paymentIntent.status === "succeeded" ||
            paymentIntent.status === "processing"
          ) {
            // Call backend to confirm payment and finalize order
            console.log("Attempting to confirm card payment...");
            await processPayment({
              paymentIntentId: paymentIntent.id,
            });
            // Success navigation is handled by the hook/slice based on backend response
            console.log(`Stripe PaymentIntent succeeded: ${paymentIntent.id}`);
          } else {
            // Handle unexpected statuses
            setPaymentError(
              `Payment ended with status: ${paymentIntent.status}. Please try again or contact support.`
            );
          }
        } else {
          // This case might occur if redirect: 'if_required' leads to no PI returned immediately
          // but usually means success or action needed handled by elements
          console.warn(
            "Stripe confirmPayment finished without returning a PaymentIntent directly."
          );
          // Potentially check elements status or wait for webhook implicitly
          // For now, assume success if no error, rely on backend confirmation
          // This might need refinement based on specific payment method flows
        }
      }
    } catch (err) {
      console.error("Payment processing error:", err);
      setPaymentError(
        err.response?.data?.message || err.message || "Payment failed."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle bank transfer button click
  const handleBankTransferClick = (event) => {
    event.preventDefault();
    if (onBankTransferSubmit && typeof onBankTransferSubmit === "function") {
      onBankTransferSubmit();
    }
  };

  // Determine if the payment button should be disabled
  const isGenericPaymentDisabled =
    isProcessing ||
    loading ||
    (checkoutData.paymentMethod === PAYMENT_METHODS.CREDIT_CARD &&
      (!stripe || !elements || !cardComplete));

  // Specific button enable/disable logic for different methods
  const getButtonDisabledState = () => {
    switch (checkoutData.paymentMethod) {
      case PAYMENT_METHODS.CREDIT_CARD:
        return isGenericPaymentDisabled;
      case PAYMENT_METHODS.TWINT:
        return isProcessing || loading || !stripe;
      case PAYMENT_METHODS.STRIPE_BANK_TRANSFER:
        return isProcessing || loading || !nextAction;
      case PAYMENT_METHODS.BANK_TRANSFER_MANUAL:
        return isProcessing || loading || !paymentInstructions; // Button enabled only when instructions are loaded
      default:
        return true; // Disable if method unknown
    }
  };

  const getButtonText = () => {
    if (isProcessing) return <CircularProgress size={24} color="inherit" />;

    switch (checkoutData.paymentMethod) {
      case PAYMENT_METHODS.CREDIT_CARD:
        return tCheckout("payCreditCard", {
          amount: checkoutData.total?.toFixed(2) || "",
          currency: "CHF",
        });
      case PAYMENT_METHODS.TWINT:
        return tCheckout("continueTwintPayment");
      case PAYMENT_METHODS.STRIPE_BANK_TRANSFER:
      case PAYMENT_METHODS.BANK_TRANSFER_MANUAL:
        return tCheckout("confirmOrderAwaitingTransfer");
      default:
        return tCheckout("proceed");
    }
  };

  // Determine which payment UI to show
  let paymentContent = null;
  if (checkoutData.paymentMethod === "credit_card") {
    paymentContent = (
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          {tCheckout("enterPaymentDetails", {
            amount: formatCurrency(amountDue),
          })}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={handleCardChange}
          />
        </Paper>
        {paymentError && <Alert severity="error">{paymentError}</Alert>}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={getButtonDisabledState()}
          >
            {getButtonText()}
          </Button>
        </Box>
      </Box>
    );
  } else if (checkoutData.paymentMethod === "twint") {
    paymentContent = (
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          {tCheckout("readyToPayTwint", { amount: formatCurrency(amountDue) })}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {tCheckout("twintRedirectInfo")}
        </Alert>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            {tCheckout("twintExplanation")}
          </Typography>
          <Typography variant="body2">
            {tCheckout("twintRedirectExplanation")}
          </Typography>
        </Paper>
        {paymentError && <Alert severity="error">{paymentError}</Alert>}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={getButtonDisabledState()}
          >
            {getButtonText()}
          </Button>
        </Box>
      </Box>
    );
  } else if (checkoutData.paymentMethod === "stripe_bank_transfer") {
    paymentContent = (
      <Box>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          {tCheckout("amountDueAfterWallet", {
            amount: formatCurrency(amountDue),
          })}
        </Typography>
        <StripeBankTransferInstructions nextAction={nextAction} />
        <Alert severity="info" sx={{ mt: 2 }}>
          {tCheckout("orderWillBeProcessed", { orderNumber })}
        </Alert>
      </Box>
    );
  } else if (checkoutData.paymentMethod === "bank_transfer_manual") {
    paymentContent = (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {tCheckout("amountDueAfterWallet", {
            amount: formatCurrency(amountDue),
          })}
        </Typography>
        <ManualBankTransferInstructions instructions={paymentInstructions} />
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleBankTransferClick}
            disabled={getButtonDisabledState()}
          >
            {getButtonText()}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {tCheckout("paymentInformation")}
      </Typography>
      {paymentContent}
    </Box>
  );
};

export default PaymentStep;
